import type { Vec2 } from '../../../../geo/types';

/**
 * The Regulierspoort of 1480-1487 as it stood in 1538 (docs/plans/landmarks.md, "Regulierspoort"):
 * two round towers with a gatehouse between them and no voorpoort (BMZ). In the layout's frame of the
 * gate (site-geo's record): u along the passage from the city toward the field, v to its left, the
 * origin on the passage's middle.
 *
 * Plan: the west tower's base survives in the Munttoren, whose round face fits a circle of 2.74 m
 * (OSM way 57862728, nodes 5-31); the gatehouse adjoins its east side at the point the record gives,
 * and stands on the dry ground the record gives the gate, its field face where the bridge starts.
 * The east tower is not found: it mirrors the west one across the passage, where the record's
 * width of 16 m puts it (L).
 *
 * Heights: no drawing gives them. The painting (scan 1290-1415 x 1110-1375) draws the gatehouse as a
 * tall block with corner turrets under a hipped roof with two needles, and a round tower under a
 * cone before it; the woodcut (uva 490-630 x 1110-1260) the same block with corner turrets and two
 * needles. Each height is the painted row over the painted foot (row 1372) as a share of the
 * gatehouse's eave (row 1195), the round towers' by their own painted proportion (their wall 2.14
 * times their width, the gatehouse's 2.77 times its face), with the gatehouse's eave at 16 m: the
 * painting's proportions give about 20 m and the woodcut's about 13 m. All heights L.
 */
export const REGULIERSPOORT_ID = 'gate-regulierspoort';

export const REGULIERSPOORT_FRAME = { origin: [48.0, -672.8] as Vec2, heading: -1.1423 } as const;

/** The record's box of the gate, both towers across it: where the town walls stop (walls.ts). */
export const LAYOUT_BOX = { depth: 9.0, width: 16.0 } as const;

export const GATEHOUSE = {
  /** From the city front to the field face at the bridge's start (u 4.52). */
  u: [-4.5, 4.5] as const,
  /** From the tower's east side, where the record puts the gatehouse against it, mirrored. */
  v: [-3.84, 3.84] as const,
  eave: 16.0,
  /** The roof's eaves stand out over the walls, closed underneath. */
  overhang: 0.3,
  /** A hipped roof, its ridge along the passage, a needle on each of its ends (painted row 1117). */
  ridge: 21.5,
  needleTop: 23.1,
} as const;

/** The passage: 4 m wide as the record draws it; its arch is not measured (as the Sint Antoniespoort's). */
export const REGULIER_PASSAGE = { v: [-2, 2] as const, springing: 3.4 } as const;

/** Octagonal bartizans on the gatehouse's corners, corbelled out under the eaves (painted rows 1155-1195). */
export const TURRET = {
  radius: 1.0,
  corbelFoot: 12.8,
  corbelTop: 13.6,
  corbelRadius: 0.3,
  top: 17.4,
  spire: 19.6,
  finialTop: 20.2,
} as const;

export interface RegulierTower {
  readonly name: string;
  readonly centre: Vec2;
  readonly radius: number;
  /** Plan angle the tower's outer side turns to, counter-clockwise from u. */
  readonly facing: number;
}

/* OSM's circle about (41.52, -672.31) in the gate's frame. */
const WEST: Vec2 = [-3.14, -5.69];

export const REGULIER_TOWERS: readonly RegulierTower[] = [
  { name: 'west tower (the Munttoren base)', centre: WEST, radius: 2.74, facing: -Math.PI / 2 },
  { name: 'east tower', centre: [WEST[0], -WEST[1]], radius: 2.74, facing: Math.PI / 2 },
];

/** Its eaves (painted row 1297) under a moulded cornice, and a cone (to row 1255). */
export const REGULIER_TOWER = {
  segments: 24,
  eave: 8.2,
  cornice: { depth: 0.3, reach: 0.12 },
  apex: 12.8,
  finialTop: 13.6,
} as const;

/** Two storeys of arched windows on each face of the gatehouse, as the woodcut draws them (L for their sizes). */
export const GATEHOUSE_WINDOWS = {
  width: 0.8,
  rows: [
    { sill: 7.0, head: 8.2 },
    { sill: 11.0, head: 12.2 },
  ],
  /** Across the fronts, either side of the passage; along the flanks, clear of the towers. */
  fronts: [-2.3, 2.3],
  flanks: [2.2],
} as const;
